import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  Control,
  DeepMap,
  FieldError,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { DateTimePickerField, Icons, InformationModal, Switch } from 'utoniq-ui'

import {
  Maybe,
  TicketItemMetadataThroughTicketInput,
} from '@/clients/utoniq-core/schema'
import { CreateItemProps } from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

import { TextInputCell } from './Commons'

type ThroughTicketInputCellProps = {
  editable?: boolean
  isThroughTicket: boolean
  control: Control<CreateItemProps>
  setValue: UseFormSetValue<CreateItemProps>
  errors?: DeepMap<
    Maybe<TicketItemMetadataThroughTicketInput[]> | undefined,
    FieldError
  >
  watch: UseFormWatch<CreateItemProps>
  register: UseFormRegister<CreateItemProps>
}
export const ThroughTicketInputCell: Cell<ThroughTicketInputCellProps> = ({
  editable,
  isThroughTicket,
  control,
  setValue,
  errors,
  watch,
  register,
}) => {
  const {
    fields,
    append,
    remove: onRemove,
  } = useFieldArray({
    control,
    keyName: '_id',
    name: 'metadata.ticket.throughTicket',
  })

  const { t } = useTranslation('consoleItemCreate')
  return (
    <CellFrame sx={{ pb: 7 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{t('throughTicketLabel')}</Typography>
        <Switch
          checked={isThroughTicket}
          disabled={!editable}
          sx={{
            '&& .Mui-disabled + .MuiSwitch-track': { bgcolor: 'grey.100' },
          }}
          onChange={() => {
            if (
              !isThroughTicket &&
              (watch('metadata.ticket.throughTicket') as any[])?.length === 0
            ) {
              setValue(
                'metadata.ticket.throughTicket',
                [{ name: '' }] as never,
                { shouldValidate: true, shouldDirty: true }
              )
            }
            setValue(
              'metadata.ticket.isThroughTicket',
              !isThroughTicket as never,
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            )
          }}
        />
      </Stack>
      <Stack sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('throughTicketDesc')}
        </Typography>
      </Stack>
      {isThroughTicket && (
        <TableContainer sx={{ py: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="normal"
                  sx={{ width: 'auto', pl: 0, pr: 2 }}
                >
                  <Typography variant="subtitle1">
                    {t('subTicketAdd')}
                  </Typography>
                </TableCell>
                <TableCell
                  padding="normal"
                  sx={{ width: 30, px: 0, textAlign: 'center' }}
                >
                  {fields.length < 3 && (
                    <IconButton
                      sx={{
                        bgcolor: 'primary.main',
                        width: 20,
                        height: 20,
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          opacity: 0.8,
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'grey.100',
                          color: 'grey.300',
                        },
                      }}
                      size="small"
                      disabled={!editable}
                      onClick={() => append({ name: '' })}
                    >
                      <Icons.Plus
                        sx={{ color: 'inherit', width: 10, height: 10 }}
                      />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <SubTicketRowCell
                  key={field._id}
                  index={index}
                  register={register}
                  editable={!!editable}
                  error={errors?.[index]}
                  value={watch(`metadata.ticket.throughTicket.${index}`)}
                  setValue={setValue}
                  onRemove={() => onRemove(index)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CellFrame>
  )
}

type SubTicketRowCellProps = {
  index: number
  setValue: UseFormSetValue<CreateItemProps>
  error?: {
    name?: FieldError
    startDateOfUse?: FieldError
    endDateOfUse?: FieldError
  }
  editable: boolean
  register: ThroughTicketInputCellProps['register']
  onRemove: () => void
  value: {
    name?: string
    startDateOfUse?: Date
    endDateOfUse?: Date
  }
}
const SubTicketRowCell: Cell<SubTicketRowCellProps> = ({
  register,
  index,
  setValue,
  value,
  error,
  editable,
  onRemove,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [open, setOpen] = useState<boolean>(false)
  return (
    <>
      <TableRow
        sx={theme => ({
          borderTop:
            index !== 0 ? `1px solid ${theme.palette.grey[100]}` : 'none',
        })}
      >
        <TableCell
          padding="normal"
          sx={{
            width: 'auto',
            borderBottom: 'none',
            pl: 0,
            pr: 2,
            pb: 0,
          }}
        >
          <Typography variant="subtitle1" color="text.primary" mb={2}>
            {t('subTicketName')}
          </Typography>
          <TextInputCell
            type="text"
            titleProps={{ sx: { display: 'none' } }}
            textFieldProps={{
              sx: { m: 0 },
              InputProps: {
                inputProps: { sx: { px: 4, py: 3, mb: 0 } },
              },
              placeholder: t('subTicketNamePlaceholder'),
            }}
            {...register(`metadata.ticket.throughTicket.${index}.name`)}
            onChange={async e => {
              setValue(
                `metadata.ticket.throughTicket.${index}.name` as any,
                e.target.value,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
            }}
            error={error?.name}
          />
        </TableCell>
        <TableCell
          padding="normal"
          sx={{ width: 30, borderBottom: 'none', px: 0, pb: 0 }}
          rowSpan={3}
        >
          <Stack sx={{ pb: 5 }}>
            <IconButton
              size="small"
              disabled={!editable}
              sx={{
                color: 'grey.600',
                '&.Mui-disabled': { color: 'grey.300' },
              }}
              onClick={() => {
                if (!value?.name) {
                  onRemove()
                } else {
                  setOpen(true)
                }
              }}
            >
              <Icons.Trash sx={{ color: 'inherit' }} />
            </IconButton>
            <InformationModal
              open={open}
              title={t('removeSubTicketTitle', {
                name:
                  (value?.name?.length ?? 0) <= 20
                    ? value?.name ?? ''
                    : `${value?.name?.slice(0, 20)}…`,
              })}
              content={t('removeSubTicketDesc')}
              onClose={() => setOpen(false)}
              cancelText={t('close')}
              buttons={
                <Button
                  size="large"
                  color="primary"
                  sx={{ minWidth: 212 }}
                  onClick={() => {
                    onRemove()
                    setOpen(false)
                  }}
                >
                  {t('remove')}
                </Button>
              }
            />
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          padding="normal"
          sx={{
            width: 'auto',
            borderBottom: 'none',
            pl: 0,
            pr: 2,
            py: 0,
          }}
        >
          <Typography variant="subtitle1" color="text.primary">
            {t('availableDateStartLabel')}
          </Typography>
          <DateTimePickerField
            placeholder={t('datePlaceholder')}
            error={error?.startDateOfUse}
            value={value.startDateOfUse ? dayjs(value.startDateOfUse) : null}
            onChange={date => {
              setValue(
                `metadata.ticket.throughTicket.${index}.startDateOfUse`,
                (convertDatePickerValueToDate(date) || '') as never,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          padding="normal"
          sx={{
            width: 'auto',
            borderBottom: 'none',
            pl: 0,
            pr: 2,
            py: 0,
          }}
        >
          <Typography variant="subtitle1" color="text.primary">
            {t('availableDateEndLabel')}
          </Typography>
          <DateTimePickerField
            placeholder={t('datePlaceholder')}
            error={error?.endDateOfUse}
            value={value.endDateOfUse ? dayjs(value.endDateOfUse) : null}
            onChange={date => {
              setValue(
                `metadata.ticket.throughTicket.${index}.endDateOfUse`,
                (convertDatePickerValueToDate(date) || '') as never,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
            }}
          />
        </TableCell>
      </TableRow>
    </>
  )
}
